import React, { useState, useEffect, useCallback } from 'react';
import { makeAuthenticatedRequest } from '../../core/api/client';

const CustomerPriceHistoryTab = ({ customerName }) => {
  const [grade, setGrade] = useState('');
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPrices = useCallback(async (gradeFilter) => {
    if (!customerName) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ customer: customerName });
      if (gradeFilter) params.append('grade', gradeFilter);
      const response = await makeAuthenticatedRequest(`/api/v1/enquiries/price-history?${params}`);
      if (!response.ok) throw new Error('Failed to load price history');
      const data = await response.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load price history. Please try again.');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [customerName]);

  useEffect(() => { fetchPrices(''); setGrade(''); }, [fetchPrices]);

  const rowsFiltered = rows.filter((r) => {
    const d = (r.date || '').slice(0, 10);
    if (dFrom && d && d < dFrom) return false;
    if (dTo && d && d > dTo) return false;
    return true;
  });
  const grades = [...new Set(rowsFiltered.map((r) => r.grade).filter(Boolean))];
  const prices = rowsFiltered.map((r) => parseFloat(r.price_offer)).filter((p) => !isNaN(p) && p > 0);
  const minPrice = prices.length ? Math.min(...prices).toFixed(0) : '—';
  const maxPrice = prices.length ? Math.max(...prices).toFixed(0) : '—';
  const avgPrice = prices.length ? (prices.reduce((a, b) => a + b, 0) / prices.length).toFixed(0) : '—';

  const summary = [
    { label: 'Records found', value: rows.length, tone: 'text-gray-900', icon: '📋' },
    { label: 'Unique grades', value: grades.length, tone: 'text-blue-700', icon: '🔬' },
    { label: 'Lowest price', value: minPrice, tone: 'text-green-700', icon: '⬇️' },
    { label: 'Highest price', value: maxPrice, tone: 'text-red-700', icon: '⬆️' },
    { label: 'Average price', value: avgPrice, tone: 'text-orange-600', icon: '📊' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900">
        <strong>Tip:</strong> These are the prices quoted to {customerName} in the past — useful before sending a new
        quotation to stay consistent.
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Filter by grade</label>
          <input
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchPrices(grade.trim())}
            placeholder="Grade (optional)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">From</label>
          <input type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">To</label>
          <input type="date" value={dTo} onChange={(e) => setDTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <button
          onClick={() => fetchPrices(grade.trim())}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          {isLoading ? 'Searching...' : 'Apply'}
        </button>
        {(grade || dFrom || dTo) && (
          <button
            onClick={() => { setGrade(''); setDFrom(''); setDTo(''); fetchPrices(''); }}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>}

      {isLoading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
          Loading price history...
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <div className="text-3xl mb-2">🔍</div>
          <p className="text-sm text-gray-600">
            No price records found for <strong>{customerName}</strong>
            {grade ? ` at grade ${grade}` : ''}.
          </p>
          <p className="text-xs text-gray-400 mt-1">This customer may not have received a quoted price yet.</p>
        </div>
      )}

      {!isLoading && rows.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {summary.map(({ label, value, tone, icon }) => (
              <div key={label} className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{icon} {label}</p>
                <p className={`text-xl font-bold ${tone}`}>{value}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {grades.map((g) => {
              const gradeRows = rowsFiltered.filter((r) => r.grade === g);
              const gradePrices = gradeRows.map((r) => parseFloat(r.price_offer)).filter((p) => !isNaN(p) && p > 0);
              const avg = gradePrices.length
                ? (gradePrices.reduce((a, b) => a + b, 0) / gradePrices.length).toFixed(0)
                : null;
              return (
                <div key={g} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
                  <span className="font-semibold text-gray-700">{g}</span>
                  <span className="text-gray-500 ml-2">{gradeRows.length} quotes</span>
                  {avg && <span className="text-orange-600 font-semibold ml-2">avg {avg}</span>}
                </div>
              );
            })}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Date', 'Grade', 'Size (mm)', 'Price', 'Unit', 'Qty', 'UOM', 'Customer Price', 'Result'].map((h) => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rowsFiltered.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{r.date?.slice(0, 10) || '—'}</td>
                      <td className="px-4 py-2 font-semibold text-gray-700">{r.grade || '—'}</td>
                      <td className="px-4 py-2 text-gray-600">{r.size_mm || '—'}</td>
                      <td className="px-4 py-2 font-bold text-orange-600">{r.price_offer || '—'}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{r.price_uom || '—'}</td>
                      <td className="px-4 py-2 text-gray-600">{r.qty || '—'}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{r.uom || '—'}</td>
                      <td className="px-4 py-2 font-bold text-blue-600">{r.customer_price || '—'}</td>
                      <td className="px-4 py-2 text-xs">
                        {r.stage === 'ORDER_WON' ? (
                          <span className="text-green-700">Won</span>
                        ) : r.stage === 'ORDER_LOST' ? (
                          <span className="text-red-600">Lost{r.loss_reason_code ? ' - ' + r.loss_reason_code.replace(/_/g, ' ') : ''}</span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 text-right">
              {rows.length} record{rows.length !== 1 ? 's' : ''} for {customerName}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerPriceHistoryTab;
